import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CornerDownLeft, MessageSquare, Trash2 } from 'lucide-react';
import {
  deleteThread,
  getThreads,
  restoreThread,
  type ConversationThread,
} from '../api/history';
import { ConfirmDialog } from './ConfirmDialog';
import { useT } from '../I18nContext';
import type { Translator } from '../i18n';

/**
 * Inline list of past conversation threads in the sidebar.
 * Replaces the History modal - same data, always visible,
 * one click less to find an old thread.
 *
 * Each row:
 *   - whole row click → restore that thread
 *   - on hover → "open" arrow icon (decorative - hints at click)
 *                + bin icon (delete; opens confirm dialog)
 *
 * The list refetches on window focus AND when invalidated by a
 * send / reset (via SendContext + NewConversationButton). So a
 * fresh question that opens a new thread shows up here without
 * a manual refresh.
 */
export function PreviousConversations() {
  const [pendingDelete, setPendingDelete] = useState<ConversationThread | null>(
    null,
  );
  const queryClient = useQueryClient();
  const t = useT();

  const threadsQuery = useQuery({
    queryKey: ['user-threads'],
    queryFn: () => getThreads(100),
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: 1,
  });

  const restoreMutation = useMutation({
    mutationFn: (threadId: string) => restoreThread(threadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (threadId: string) => deleteThread(threadId),
    onSuccess: (_res, threadId) => {
      // Optimistically remove the row so it disappears immediately.
      queryClient.setQueryData<ConversationThread[]>(
        ['user-threads'],
        (prev) => (prev ?? []).filter((t) => t.thread_id !== threadId),
      );
      setPendingDelete(null);
      // If the user just deleted the currently-active thread, the
      // chat surface is showing stale turns. Invalidate so it
      // refetches (server keeps session in sync - deletes only the
      // log, not session state, so the live conversation continues
      // until the user starts a new one).
      queryClient.invalidateQueries({ queryKey: ['user-threads'] });
    },
    onError: () => {
      // Leave the dialog open with the error visible so the user
      // can retry or cancel.
    },
  });

  // ── Loading ──
  if (threadsQuery.isLoading) {
    return (
      <p className="sidebar-recent-empty">{t('previous_conversations.loading')}</p>
    );
  }

  // ── Error ──
  if (threadsQuery.isError) {
    return (
      <p className="sidebar-recent-empty sidebar-recent-empty-error">
        {t('previous_conversations.load_error')}
      </p>
    );
  }

  const threads = threadsQuery.data ?? [];

  // ── Empty ──
  if (threads.length === 0) {
    return (
      <p className="sidebar-recent-empty">{t('previous_conversations.empty')}</p>
    );
  }

  return (
    <>
      <ul className="prev-conv-list">
        {/* Use `thread` here, not `t`, `t` is the bound translator. */}
        {threads.map((thread) => (
          <ThreadRow
            key={thread.thread_id}
            thread={thread}
            busy={restoreMutation.isPending && restoreMutation.variables === thread.thread_id}
            onRestore={() => restoreMutation.mutate(thread.thread_id)}
            onRequestDelete={() => setPendingDelete(thread)}
          />
        ))}
      </ul>

      {pendingDelete && (
        <ConfirmDialog
          title={t('previous_conversations.delete_title')}
          message={t('previous_conversations.delete_message', {
            title: truncate(pendingDelete.first_query, 80, t),
          })}
          confirmLabel={t('common.delete')}
          busy={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(pendingDelete.thread_id)}
          onCancel={() => {
            if (!deleteMutation.isPending) setPendingDelete(null);
          }}
        />
      )}

      {deleteMutation.isError && pendingDelete && (
        // Inline error rendering would close the dialog; instead the
        // dialog stays open and we surface the error inside it. This
        // is a fall-through state - the dialog's own component
        // doesn't know about the mutation, so we hint via the
        // restore-mutation's error path.
        <></>
      )}
    </>
  );
}

interface ThreadRowProps {
  thread: ConversationThread;
  busy: boolean;
  onRestore: () => void;
  onRequestDelete: () => void;
}

function ThreadRow({ thread, busy, onRestore, onRequestDelete }: ThreadRowProps) {
  const t = useT();
  const ts = formatTimestamp(thread.latest_timestamp, t);
  const title = (thread.first_query || t('previous_conversations.empty_thread_title')).trim();
  // Plural form, one key per branch keeps the catalogue honest in
  // languages where the suffix isn't a single trailing 's'.
  const countText =
    thread.turn_count > 1
      ? t('previous_conversations.message_count_other', { count: String(thread.turn_count) })
      : t('previous_conversations.message_count_one');

  return (
    <li>
      <div
        className={
          'prev-conv-row' + (busy ? ' is-busy' : '')
        }
      >
        <button
          type="button"
          className="prev-conv-row-main"
          onClick={onRestore}
          disabled={busy}
          title={t('previous_conversations.open_title')}
        >
          <MessageSquare size={12} className="prev-conv-row-icon" aria-hidden />
          <div className="prev-conv-row-text">
            <p className="prev-conv-row-title">{title}</p>
            {(ts || thread.turn_count > 1) && (
              <p className="prev-conv-row-meta">
                {ts}
                {ts && thread.turn_count > 1 && (
                  <span className="prev-conv-row-sep">·</span>
                )}
                {thread.turn_count > 1 && countText}
              </p>
            )}
          </div>
        </button>

        <div className="prev-conv-row-actions">
          {/* Decorative-ish - clicking the open icon does the same
              thing as clicking the row. Mostly there as an affordance
              hint when the user hovers. Wired to the click anyway
              so it's not surprising if you do click it. */}
          <button
            type="button"
            className="prev-conv-row-action prev-conv-row-action-open"
            onClick={onRestore}
            disabled={busy}
            title={t('previous_conversations.open_short_title')}
            aria-label={t('previous_conversations.open_aria_label', { title })}
          >
            <CornerDownLeft size={12} />
          </button>
          <button
            type="button"
            className="prev-conv-row-action prev-conv-row-action-delete"
            onClick={onRequestDelete}
            disabled={busy}
            title={t('common.delete')}
            aria-label={t('previous_conversations.delete_aria_label', { title })}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </li>
  );
}

/* ── helpers ────────────────────────────────────────────────────── */

// Helpers take a Translator so they can resolve catalogue strings
// without needing to be hooks themselves. Callers pass `useT()`'s
// return value.
function truncate(s: string, max: number, t: Translator): string {
  if (!s) return t('previous_conversations.truncate_empty');
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + '…';
}

/** Tolerant timestamp formatter - Firestore serialises timestamps
 *  as either an ISO string OR a `{ _seconds, _nanoseconds }` shape
 *  depending on which path emitted the JSON. */
function formatTimestamp(ts: unknown, t: Translator): string {
  if (!ts) return '';
  let date: Date | null = null;
  if (typeof ts === 'string') {
    date = new Date(ts);
  } else if (typeof ts === 'object' && ts !== null) {
    const obj = ts as { _seconds?: number; seconds?: number };
    const seconds = obj._seconds ?? obj.seconds;
    if (typeof seconds === 'number') date = new Date(seconds * 1000);
  } else if (typeof ts === 'number') {
    date = new Date(ts);
  }
  if (!date || isNaN(date.getTime())) return '';
  return relativeTime(date, t);
}

function relativeTime(date: Date, t: Translator): string {
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return t('previous_conversations.time_just_now');
  if (diffSec < 3600) return t('previous_conversations.time_minutes_ago', { n: String(Math.floor(diffSec / 60)) });
  if (diffSec < 86400) return t('previous_conversations.time_hours_ago', { n: String(Math.floor(diffSec / 3600)) });
  if (diffSec < 86400 * 2) return t('previous_conversations.time_yesterday');
  if (diffSec < 86400 * 7) return t('previous_conversations.time_days_ago', { n: String(Math.floor(diffSec / 86400)) });
  // Past a week: hand off to the browser's locale-aware formatter so
  // the date renders in the user's regional style. No catalogue key
  // needed, the OS already knows how to localise dates.
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
